import { FC, MouseEvent, useEffect, useCallback, useReducer } from 'react';
import { graphql } from 'react-relay/legacy';
import { useFragment, useRelayEnvironment } from 'react-relay';
import { useDispatch } from 'react-redux';
import SV from 'server-vars';
import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import { Locale, defaultLocale, LOCALE_PSEUDO } from 'dibs-intl/exports/constants';
import { getSupportedLocales } from 'dibs-intl/exports/intlSupport';
import { handleLocaleUrl } from 'dibs-intl/exports/handleLocaleUrl';
import getCurrencySymbol from 'dibs-currency-helpers/getCurrencySymbol';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { hasKey } from 'dibs-ts-utils/exports/hasKey';

import { Button } from 'dibs-elements/exports/Button';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';

import { setLocalCurrency } from '../../../utils/preferredCurrency';
import { actionCreators } from '../../../actions';
import { changeDisplayPreferrencesMutation } from './changeDisplayPreferrencesMutation';
import { setLangCookie } from '../../../mutations/setLangCookieMutation';
import { useCurrency } from '../../CurrencyProvider/useCurrency';
import { trackFooterNavigationClick } from '../../../helpers/trackingHelpers';
import { DisplaySettingsModal_viewer$key } from './__generated__/DisplaySettingsModal_viewer.graphql';
import { DisplaySettingsModal_user$key } from './__generated__/DisplaySettingsModal_user.graphql';
import { intlCountries } from '../messages/intlCountries';

import styles from './DisplaySettingsModal.scss';

type Props = {
    viewer: DisplaySettingsModal_viewer$key;
    user: DisplaySettingsModal_user$key;
};

type FormState = {
    currency: string;
    preferredLocale: Locale;
    isDirty: boolean;
    isOpen: boolean;
    isDisabled: boolean;
};

type FormAction =
    | { type: 'open' }
    | { type: 'close' }
    | { type: 'disable' }
    | { type: 'setPreferredLocale'; payload: { preferredLocale: Locale } }
    | { type: 'setCurrency'; payload: { currency: string; initialize?: boolean } };

function reducer(state: FormState, action: FormAction): FormState {
    switch (action.type) {
        case 'setPreferredLocale':
            return { ...state, isDirty: true, preferredLocale: action.payload.preferredLocale };
        case 'setCurrency':
            return {
                ...state,
                isDirty: !action.payload.initialize,
                currency: action.payload.currency,
            };
        case 'open':
            return { ...state, isOpen: true };
        case 'close':
            return { ...state, isOpen: false };
        case 'disable': {
            return { ...state, isDisabled: true };
        }

        default:
            throw new Error('using wrong action type in DisplaySettingsModal');
    }
}

function isIntlKey<O>(key: unknown): key is keyof O {
    return !!key && typeof key === 'string' && hasKey(intlCountries, key);
}

const messages = defineMessages({
    header: {
        id: 'dbl.Footer.DisplaySettingsModal.header',
        defaultMessage: 'Update Display Settings',
    },
    description: {
        id: 'dbl.Footer.DisplaySettingsModal.description',
        defaultMessage: 'Select the language and currency you’d like to see.',
    },
});

const DisplaySettingsModal: FC<Props> = ({ viewer: viewerRef, user: userRef }) => {
    const environment = useRelayEnvironment();
    const viewer = useFragment(
        graphql`
            fragment DisplaySettingsModal_viewer on Viewer {
                currencies
                intlMvpEnabled: featureFlag(feature: "intlMVP2022")
                isSegmentedIntlUrlsEnabled: featureFlag(feature: "segmentedIntlUrls")
            }
        `,
        viewerRef
    );
    const user = useFragment(
        graphql`
            fragment DisplaySettingsModal_user on User {
                id
                serviceId
                profile {
                    currency
                }
            }
        `,
        userRef
    );

    const intl = useIntl();
    const dispatch = useDispatch();

    const { currency: currentCurrency, showCurrency } = useCurrency();

    const [formState, formDispatch] = useReducer(reducer, {
        currency: currentCurrency,
        preferredLocale: intl.locale as Locale,
        isDirty: false,
        isOpen: false,
        isDisabled: false,
    });

    useEffect(() => {
        formDispatch({
            type: 'setCurrency',
            payload: {
                currency: currentCurrency,
                initialize: true,
            },
        });
    }, [currentCurrency]);

    const handleModalClose = useCallback(() => {
        if (formState.isDisabled) {
            return;
        }
        formDispatch({ type: 'close' });
    }, [formState.isDisabled]);

    const onPreferrencesUpdateComplete = useCallback(
        (currency: string, locale: Locale) => {
            setLocalCurrency(currency);
            dispatch(actionCreators.populateCurrency(currency));

            const isSegmentedIntlUrlsEnabled = viewer?.isSegmentedIntlUrlsEnabled;

            const hasUrlParams = document.location.search !== '';
            const urlParams = new URLSearchParams(document.location.search);
            const hasCurrencyQp = urlParams.get('currency');

            if (hasCurrencyQp) {
                urlParams.set('currency', formState.currency);
            }

            if (isSegmentedIntlUrlsEnabled) {
                let url = '';

                if (hasUrlParams) {
                    url = document.location.pathname + '?' + urlParams.toString();
                } else {
                    url = document.location.pathname;
                }

                window.location.replace(handleLocaleUrl(url, locale));
            } else {
                const hasCurrencyChanged = formState.currency !== currentCurrency;
                const hasLocaleChanged = formState.preferredLocale !== intl.locale;
                // if only currency changed reload the page without redirect to localized page
                if (hasCurrencyChanged && !hasLocaleChanged) {
                    if (hasCurrencyQp) {
                        window.location.search = urlParams.toString(); // reloads page
                    } else {
                        window.location.reload();
                    }
                    return;
                }

                // TODO INTL: remove code bellow once we enable segmented intl urls
                const defaultDomainsLinks = SV.get('defaultDomainsLinks');
                const domains = SV.get('domainLinks');
                const domainLinks: { hrefLang: Locale; href: string }[] =
                    domains || defaultDomainsLinks || [];
                const domainLink = domainLinks.find(link => link.hrefLang === locale);

                let url;

                if (hasUrlParams) {
                    url = domainLink?.href + '?' + urlParams.toString();
                } else {
                    url = domainLink?.href;
                }

                location.assign(handleLocaleUrl(url || '/'));
            }
        },
        [
            currentCurrency,
            dispatch,
            formState.currency,
            formState.preferredLocale,
            intl.locale,
            viewer,
        ]
    );

    const handleFormSubmit = useCallback(
        async (event: MouseEvent) => {
            formDispatch({ type: 'disable' });

            const hasCurrencyChanged = formState.currency !== currentCurrency;
            const hasLocaleChanged = formState.preferredLocale !== intl.locale;

            if (hasLocaleChanged && viewer?.intlMvpEnabled) {
                await setLangCookie(environment, {
                    locale: formState.preferredLocale,
                    cookieDomain: SV.get('cookieDomain'),
                });
            }

            if (hasCurrencyChanged) {
                trackFooterNavigationClick(
                    {
                        label: `change currency|${formState.currency}`,
                        isInteractiveEvent: true,
                        additional: { defaultCurrency: `${formState.currency} Selected` },
                    },
                    event
                );
            }

            if (user) {
                changeDisplayPreferrencesMutation({
                    environment,
                    user: user,
                    currency: hasCurrencyChanged ? formState.currency : null,
                    preferredLocale: hasLocaleChanged ? formState.preferredLocale : null,
                    onCompleted: () =>
                        onPreferrencesUpdateComplete(formState.currency, formState.preferredLocale),
                });
            } else {
                onPreferrencesUpdateComplete(formState.currency, formState.preferredLocale);
            }
        },
        [
            formState.currency,
            formState.preferredLocale,
            currentCurrency,
            intl.locale,
            viewer,
            user,
            environment,
            onPreferrencesUpdateComplete,
        ]
    );

    const countries = getSupportedLocales()
        .map(locale => {
            if (locale === LOCALE_PSEUDO) {
                return null;
            }

            return {
                label: intl.formatMessage(intlCountries[locale]),
                value: locale,
            };
        })
        .filter(filterNulls);

    const currencies = (
        viewer?.currencies?.map(currency => {
            const cuurrencySymbol = getCurrencySymbol(currency);
            return currency
                ? {
                      label: cuurrencySymbol ? `${cuurrencySymbol} ${currency}` : currency,
                      value: currency,
                  }
                : null;
        }) || []
    ).filter(filterNulls);

    const countryMessage = isIntlKey(intl.locale)
        ? intlCountries[intl.locale]
        : intlCountries[defaultLocale];

    if (!showCurrency) {
        return <div className={styles.placeholder} />;
    }

    return (
        <div>
            <div className={styles.modalButtonContainer}>
                <Button
                    className={styles.modalButton}
                    onClick={() => formDispatch({ type: 'open' })}
                    type="textCta"
                    size="small"
                    dataTn="display-settings-modal-cta"
                >
                    {intl.formatMessage(countryMessage)} |{' '}
                    {currencies.find(c => c.value === currentCurrency)?.label}
                </Button>
            </div>
            <ModalContainer
                isOpen={formState.isOpen}
                ariaLabel={intl.formatMessage(messages.header)}
                onClose={handleModalClose}
            >
                <ModalBody>
                    <BarHeader
                        className={styles.header}
                        title={intl.formatMessage(messages.header)}
                    />
                    <ModalCloseButton onClick={handleModalClose} />
                    <p className={styles.description}>{intl.formatMessage(messages.description)}</p>
                    <div className={styles.inputContainer}>
                        <BasicSelect
                            disabled={formState.isDisabled}
                            dataTn="display-settings-modal-country-select"
                            name="country-select"
                            onChange={event =>
                                formDispatch({
                                    type: 'setPreferredLocale',
                                    payload: { preferredLocale: event.target.value as Locale },
                                })
                            }
                            value={formState.preferredLocale}
                            options={countries}
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        <BasicSelect
                            disabled={formState.isDisabled}
                            dataTn="display-settings-modal-currency-select"
                            name="currency-select"
                            onChange={event =>
                                formDispatch({
                                    type: 'setCurrency',
                                    payload: {
                                        currency: event.target.value,
                                    },
                                })
                            }
                            options={currencies}
                            value={formState.currency}
                        />
                    </div>
                    <Button
                        fullWidth
                        dataTn="display-settings-modal-submit"
                        disabled={formState.isDisabled || !formState.isDirty}
                        onClick={handleFormSubmit}
                    >
                        <FormattedMessage
                            id="dbl.Footer.DisplaySettingsModal.submit"
                            defaultMessage="Submit"
                        />
                    </Button>
                </ModalBody>
            </ModalContainer>
        </div>
    );
};

export default DisplaySettingsModal;
